import { ReactElement } from 'react';
import { IPageDoctorFields, IPageLocationFields } from 'types/contentful';
import Image from '../Image';
import ScheduleAppointmentCta from '../ScheduleAppointmentCta';
import Link from '../Link';

interface DoctorImageAndPageSummaryProps {
  content: IPageDoctorFields;
  officeInfo: IPageLocationFields | null;
  doctorNameHeadingType: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  displayAnchorLinks: ReactElement | undefined;
  siteName: string;
  factua?: boolean;
}

export default function DoctorImageAndPageSummary({
  content,
  officeInfo,
  doctorNameHeadingType = 'h1',
  displayAnchorLinks,
  siteName = '',
  factua = false,
}: DoctorImageAndPageSummaryProps): ReactElement {
  const locationAddressArray = [
    officeInfo?.address1,
    officeInfo?.address2,
    officeInfo?.address3,
    officeInfo?.city,
    officeInfo?.state,
    officeInfo?.zipCode,
  ];
  const locationAddress = locationAddressArray.filter(Boolean).join(', ');

  return (
    <div className="px-site-component mx-auto bg-white pt-5 pb-10 md:pt-10 md:pb-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 lg:gap-14 items-start">
        <div className="flex justify-center self-start">
          <div className="self-center w-4/12 md:w-full">
            {content?.doctorPhoto && (
              <Image
                image={content.doctorPhoto}
                query="w=280&h=360"
                classNames="object-cover w-full"
              />
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center">
            <div className="bordered-heading-richtext bh-doctor-name">
              {(() => {
                const Tag = (doctorNameHeadingType || 'h1') as
                  | 'h1'
                  | 'h2'
                  | 'h3'
                  | 'h4'
                  | 'h5'
                  | 'h6';
                return (
                  <Tag className="font-light md:text-5xl text-new-34 text-primary-actual md:leading-tight">
                    {content.firstName} {content.lastName}, {content.titles.join(', ')}
                  </Tag>
                );
              })()}

              <p className="font-thin text-xl md:text-2xl text-primary-actual mt-2">
                {content.typeOfDoctor} at {siteName}
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-3 mt-8">
            {officeInfo && (
              <ScheduleAppointmentCta
                location={officeInfo}
                colorScheme="Primary"
                wrapperClasses="p-0"
                text="Schedule"
                from="doctorOnly"
                factua={factua}
                providerId={content.providerId}
              />
            )}

            <div className="flex flex-col md:flex-row md:flex-wrap gap-3">{displayAnchorLinks}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10 text-lg">
            {officeInfo && (
              <div>
                <h2 className="font-heading text-xl text-primary-actual mb-3">Location</h2>
                <p className="text-primary-actual leading-relaxed">{locationAddress}</p>
              </div>
            )}

            {content.specialties && content.specialties.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-primary-actual mb-3">Specialties</h2>
                <ul className="space-y-2 text-primary-actual">
                  {content.specialties.map((specialty) => (
                    <li key={specialty.fields.service}>
                      {specialty.fields.slug ? (
                        <Link slug={`${specialty.fields.slug}`}>
                          <a className="text-secondary-actual">{specialty.fields.service}</a>
                        </Link>
                      ) : (
                        <span>{specialty.fields.service}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {content.languages && content.languages.length > 0 && (
              <div>
                <h2 className="font-heading text-xl text-primary-actual mb-3">Languages</h2>
                <ul className="space-y-2 text-primary-actual">
                  {content.languages.map((language) => (
                    <li key={language}>{language}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-4">
            <p className="font-thin text-lg text-primary-actual">
              Accepting New Patients?{' '}
              <span className="bold">{content.acceptingNewPatients ? 'Yes' : 'No'}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
